import Input from "heroic-ui-library/dist/components/Input";
import { useEffect, useRef, useState } from "react";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import isPhone from "validator/lib/isMobilePhone";
import toast from "react-hot-toast";
import { firebaseAuth } from "../../lib/firebase";
import Loading from "react-loading";
import Button from "heroic-ui-library/dist/components/Button";
import { getCountryCode } from "../../lib";
import PhoneNumberInput from "../../components/PhoneNumber";

type Props = {
  phoneNumber: string;
  countryCode: string;
  setPhoneNumber: (value: string) => void;
  setCountryCode: (value: string) => void;
  onVerificationSuccess: () => void;
};

export default function OTPVerify({
  phoneNumber,
  setPhoneNumber,
  countryCode,
  setCountryCode,
  onVerificationSuccess,
}: Props) {
  const [path, setPath] = useState("sendOTP");
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const recaptcha = useRef<RecaptchaVerifier>();
  const signInResult = useRef<ConfirmationResult>();

  useEffect(() => {
    if (
      !recaptcha.current ||
      recaptcha.current == null ||
      recaptcha.current == undefined
    ) {
      recaptcha.current = new RecaptchaVerifier(
        firebaseAuth,
        "recaptcha-container",
        {
          size: "invisible", // or 'normal', depending on your use case
          callback: () => {
            // Do something after the reCAPTCHA is solved
          },
        }
      );
    }
  }, []);

  const sendOTP = async () => {
    const completePhone = countryCode + phoneNumber.replace(/\D/g, "");
    const isPhoneValid = isPhone(completePhone, ["en-US", "en-IN"]);
    if (!isPhoneValid) return toast.error("Invalid Phone Number");

    // send otp
    setIsLoading(true);

    try {
      const confirmationResult = await signInWithPhoneNumber(
        firebaseAuth,
        countryCode + phoneNumber.replace(/\D/g, ""),
        recaptcha.current!
      );

      signInResult.current = confirmationResult;

      setPath("verifyOTP");
    } catch (error: any) {
      console.error("Error during Pin sending", error);

      if (error.code == "auth/operation-not-allowed") {
        console.log("There is an error", error.code);
        toast.error("There is an error", error.code);
      }

      if (error.code == "auth/billing-not-enabled") {
        console.log("Enable Billing in project", error.code);
        toast.error("Ask admin to enable the billing in the gcp project.");
      }

      if (error.code == "auth/too-many-requests") {
        console.log("You have tried multiple times", error.code);
        toast.error("Too many requests. Please try again later.");
      }
    }

    setIsLoading(false);
  };

  const verifyOTP = async () => {
    if (otp.length != 6) return toast.error("Invalid Pin Code");

    setIsLoading(true);

    try {
      await signInResult.current!.confirm(otp);
      onVerificationSuccess();
    } catch (error) {
      if ((error as any).code == "auth/invalid-verification-code") {
        toast.error("Pin Code is Invalid.");
      }
    }

    setIsLoading(false);
  };

  return (
    <>
      {path == "sendOTP" && (
        <div>
          <span className="font-acme text-2xl text-left mb-5">
            Enter the phone number to receive the Pin. Once your phone is
            verified, you can set the PIN.
          </span>
          <div className="flex w-full items-center space-x-4 mt-3 ">
            {getCountryCode() != "US" && (
              <select
                className="hl-input"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                <option value="+1">US (+1)</option>
                <option value="+91">IN (+91)</option>
              </select>
            )}
            <PhoneNumberInput
              value={phoneNumber}
              required
              onChange={setPhoneNumber}
              countryCode={countryCode == "+1" ? "US" : "IN"}
            />
          </div>
        </div>
      )}
      {path == "verifyOTP" && (
        <div>
          <span className="font-acme text-2xl text-left mb-5">
            Enter the Pin received on your phone number. Once your phone is
            verified, you can set the PIN.
          </span>
          <Input
            className="text-lg w-full mt-3"
            value={otp}
            onChange={(e) => setOtp(e.currentTarget.value)}
            placeholder="Enter Pin"
            type="text"
            maxLength={6}
          />
        </div>
      )}

      {!isLoading && (
        <Button
          onClick={path == "sendOTP" ? sendOTP : verifyOTP}
          btnType="green"
          className="submit-btn mt-4 mb-4"
          label={path == "sendOTP" ? "Send Pin" : "Verify Pin"}
        />
      )}
      {isLoading && (
        <div className="w-full items-center justify-center flex mt-4">
          <Loading color="#002356" type="spin" />
        </div>
      )}
    </>
  );
}
